import { HttpStatusCode } from 'axios';
import { BaseEntity } from '@/api/EntityApi';
import { ErrorCode } from '@/configs/errorCode';
import { Asset } from '@/api/AssetApi';
import { DeepKeys } from '@tanstack/react-table';
import { SerializedModulesManager } from '@/renderer/managers/ModulesManager';
import { CreatorProductModule } from '@/api/CreatorProductModuleApi';
import { Preset } from '@/api/PresetApi';
import LANGUAGES from '@/configs/languages';
import { DrawingVertex } from '@/renderer/objects/DrawingVertex';
import { RoomVertex } from '@/renderer/objects/RoomVertex';

export type Language = keyof typeof LANGUAGES;
export type ForeignLanguage = Exclude<Language, 'PL'>;
export type LowercaseLanguage = Lowercase<ForeignLanguage>;
export type Translations<TData> = Record<LowercaseLanguage, TData>;

export type Order = 'ASC' | 'DESC';

export type CustomFilters = Record<string, unknown>;

export interface Sort<TData extends BaseEntity = any> {
	key: DeepKeys<TData> | null;
	order: Order;
}

export interface ApiQueryOptions<TData extends BaseEntity> {
	page: number;
	limit: number;
	search: string;
	sortBy: Sort<TData>;
}

export interface ApiPaginatedResponse<TData extends {} = {}> {
	data: TData[];
	meta: {
		currentPage: number;
		itemsPerPage: number;
		totalItems: number;
		totalPages: number;
	};
}

export interface ApiFormattedResponseData<TData extends {} = {}> {
	data: TData[];
	totalPages: number;
}

export type ThumbnailSize = 200 | 1000;

export interface Attachment {
	id: UUID;
	path: string;
	name: string;
	size: number;
	mimetype: string;
	width: number | null;
	height: number | null;
	thumbnails: Record<ThumbnailSize, string>;
}

export type UUID = ReturnType<typeof crypto.randomUUID>;

export type ApiErrorMessage = ErrorCode | string | (ErrorCode | string)[];

export interface ApiError {
	error: string;
	message: ApiErrorMessage;
	status: HttpStatusCode;
}

export interface AppError {
	status: HttpStatusCode;
	stack: (ErrorCode | string)[];
}

export enum MouseButton {
	LEFT,
	MIDDLE,
	RIGHT,
}

export enum RotationDirection {
	CLOCKWISE = 'cw',
	COUNTER_CLOCKWISE = 'ccw',
}

export enum MessageDataTitle {
	RELAOD_WINDOW = 'RELAOD_WINDOW',
	SEND_PRESET_DATA = 'SEND_PRESET_DATA',
}

export type MessageDataPayload =
	| {
			title: MessageDataTitle.RELAOD_WINDOW;
	  }
	| {
			title: MessageDataTitle.SEND_PRESET_DATA;
			data: SavePresetData;
	  };

export enum CreatorActionType {
	SET_MATERIAL = 'SET_MATERIAL',
}

export type CreatorAction = {
	id: UUID;
	attributeValue: BaseEntity;
	type: CreatorActionType;
	payload: CreatorActionPayload;
};

export type CreatorActionPayload = {
	asset?: Asset;
	target?: string;
};

export type SavePresetData = {
	largestAvailableRectangle: Preset['largestAvailableRectangle'];
	creatorProductModules: CreatorProductModule[];
	rendererModules: SerializedModulesManager;
};

export type Vertex = DrawingVertex | RoomVertex;

export enum ChangeLevelDirection {
	UP,
	DOWN,
}
